import { create } from "zustand";
import type { AssistantConversation, ChatEvent } from "@rivets/core";

/**
 * Panel-local state for the Rivets assistant.
 *
 * The assistant is deliberately secondary: a 400px card floating over the
 * bottom-right of the workspace, never a page. Everything about a
 * *conversation* lives in the ordinary chat machinery (`useChatSubscription` +
 * `useChatStore`) because an assistant conversation is an ordinary chat; this
 * store only holds what is true of the panel itself — how it is presented,
 * which conversation is showing, is there an unread reply, and which sends have
 * not yet been echoed back by the server.
 */

/**
 * Four presentations, Linear's agent popup's: gone, a pill on the bottom edge,
 * the 400px card, and the card filled out to the content column.
 *
 * `minimized` is not `closed`: the panel is still listening, still collects an
 * unread dot, and one click puts the conversation back exactly where it was.
 */
export type AssistantPanelState = "closed" | "minimized" | "open" | "maximized";

const PANEL_STATES: readonly AssistantPanelState[] = ["closed", "minimized", "open", "maximized"];

export const ASSISTANT_PANEL_STORAGE_KEY = "rivets.assistant-panel.v2";

/** The subset of `Storage` this module needs, so tests can pass a fake. */
export type AssistantStorage = Pick<Storage, "getItem" | "setItem">;

/** True while the panel is showing its transcript — which is what "read" means
 * for the unread dot, and what decides whether the composer gets focus. */
export function assistantPanelIsExpanded(panel: AssistantPanelState): boolean {
  return panel === "open" || panel === "maximized";
}

export function readStoredPanel(storage: AssistantStorage | undefined): AssistantPanelState {
  if (!storage) return "closed";
  try {
    const stored = storage.getItem(ASSISTANT_PANEL_STORAGE_KEY);
    return stored && (PANEL_STATES as readonly string[]).includes(stored) ? stored as AssistantPanelState : "closed";
  } catch {
    return "closed";
  }
}

export function writeStoredPanel(storage: AssistantStorage | undefined, panel: AssistantPanelState): void {
  if (!storage) return;
  try {
    storage.setItem(ASSISTANT_PANEL_STORAGE_KEY, panel);
  } catch {
    /* Private mode or a full quota: the in-memory value still works. */
  }
}

function browserStorage(): AssistantStorage | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

/** List, or one conversation. There is no third screen — this is a drawer. */
export type AssistantView = { kind: "list" } | { kind: "conversation"; chatId: string };

/** A message the user sent that the transcript has not echoed back yet. */
export interface PendingAssistantMessage {
  id: string;
  chatId: string;
  text: string;
  createdAt: number;
  failed?: string;
}

interface AssistantDrawerState {
  context: string | null;
  generation: number;
  busy?: "start" | "send" | "archive" | "reset" | "cancel";
  error?: string;
  activateContext: (context: string | null) => void;
  /** The one source of truth for how the panel is presented. */
  panel: AssistantPanelState;
  /** `panel !== "closed"`, kept in sync so a caller that only cares whether the
   * assistant is on screen at all (the sidebar row's pressed state, the command
   * palette) does not have to know about the four-state machine. */
  open: boolean;
  view: AssistantView;
  /** A reply landed while the panel was not showing it. Cleared on expand. */
  unread: boolean;
  pending: PendingAssistantMessage[];
  setPanel: (panel: AssistantPanelState) => void;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  showList: () => void;
  openConversation: (chatId: string) => void;
  markUnread: () => void;
  addPending: (message: PendingAssistantMessage) => void;
  failPending: (id: string, error: string) => void;
  dropPending: (id: string) => void;
  /** Re-key an optimistic message once the server named the chat it landed in. */
  adoptPending: (id: string, chatId: string) => void;
  settlePending: (chatId: string, delivered: readonly DeliveredAssistantMessage[]) => void;
}

const initialPanel = readStoredPanel(browserStorage());

export const useAssistantDrawer = create<AssistantDrawerState>((set) => ({
  context: null,
  generation: 0,
  activateContext: (context) => set((state) => ({
    context, generation: state.generation + 1,
    view: { kind: "list" }, unread: false, pending: [], busy: undefined, error: undefined,
  })),
  panel: initialPanel,
  open: initialPanel !== "closed",
  view: { kind: "list" },
  unread: false,
  pending: [],

  setPanel: (panel) => set((state) => {
    if (state.panel === panel) return state;
    writeStoredPanel(browserStorage(), panel);
    return { panel, open: panel !== "closed", unread: assistantPanelIsExpanded(panel) ? false : state.unread };
  }),
  // "Open" from anywhere that only knows the boolean: a panel that is already
  // maximized is already open, so this must not shrink it back down.
  setOpen: (open) => set((state) => {
    const panel: AssistantPanelState = open ? (assistantPanelIsExpanded(state.panel) ? state.panel : "open") : "closed";
    if (state.panel === panel) return state;
    writeStoredPanel(browserStorage(), panel);
    return { panel, open: panel !== "closed", unread: assistantPanelIsExpanded(panel) ? false : state.unread };
  }),
  // ⌘J and the sidebar row are one switch: anything on screen goes away,
  // nothing on screen comes back as the card.
  toggle: () => set((state) => {
    const panel: AssistantPanelState = state.panel === "closed" ? "open" : "closed";
    writeStoredPanel(browserStorage(), panel);
    return { panel, open: panel !== "closed", unread: assistantPanelIsExpanded(panel) ? false : state.unread };
  }),
  showList: () => set({ view: { kind: "list" } }),
  openConversation: (chatId) => set((state) => {
    const panel: AssistantPanelState = assistantPanelIsExpanded(state.panel) ? state.panel : "open";
    if (panel !== state.panel) writeStoredPanel(browserStorage(), panel);
    return { view: { kind: "conversation", chatId }, panel, open: true, unread: false };
  }),
  // An unread mark is only meaningful while the transcript is not on screen;
  // showing it is what "reading" means here. A minimized panel still collects.
  markUnread: () => set((state) => (assistantPanelIsExpanded(state.panel) || state.unread ? state : { unread: true })),

  addPending: (message) => set((state) => ({ pending: [...state.pending, message] })),
  failPending: (id, error) => set((state) => ({
    pending: state.pending.map((message) => (message.id === id ? { ...message, failed: error } : message)),
  })),
  dropPending: (id) => set((state) => ({ pending: state.pending.filter((message) => message.id !== id) })),
  adoptPending: (id, chatId) => set((state) => ({
    pending: state.pending.map((message) => (message.id === id ? { ...message, chatId } : message)),
  })),
  settlePending: (chatId, delivered) => set((state) => {
    const pending = settledPending(state.pending, chatId, delivered);
    return pending.length === state.pending.length ? state : { pending };
  }),
}));

// -- pure helpers -------------------------------------------------------------

export interface DeliveredAssistantMessage {
  seq: number;
  text: string;
  clientRequestId?: string;
}

/** The server stores each send's `clientRequestId` on the `user.message` it
 * appends, so an echo settles exactly its own message — repeated text and
 * server-added recovery notices cannot confuse two of them. */
export function settledPending(
  pending: readonly PendingAssistantMessage[],
  chatId: string,
  delivered: readonly DeliveredAssistantMessage[],
): PendingAssistantMessage[] {
  const acknowledged = new Set(delivered.flatMap((event) => event.clientRequestId ? [event.clientRequestId] : []));
  return pending.filter((message) => message.chatId !== chatId || !acknowledged.has(message.id));
}

export function assistantContextIsCurrent(context: string, generation: number): boolean {
  const state = useAssistantDrawer.getState();
  return state.context === context && state.generation === generation;
}

/** A small tolerance avoids detaching on fractional pixel/layout rounding. */
export function isFollowingAssistantBottom(metrics: { scrollHeight: number; scrollTop: number; clientHeight: number }): boolean {
  return metrics.scrollHeight - metrics.scrollTop - metrics.clientHeight <= 40;
}

/** The optimistic messages to render under one conversation's transcript. */
export function pendingForChat(
  pending: readonly PendingAssistantMessage[],
  chatId: string,
): PendingAssistantMessage[] {
  return pending.filter((message) => message.chatId === chatId);
}

/** Most recently active first — the order a drawer list is read in. */
export function sortAssistantConversations(
  conversations: readonly AssistantConversation[],
): AssistantConversation[] {
  return [...conversations].sort((a, b) => (b.lastActivityAt ?? b.createdAt) - (a.lastActivityAt ?? a.createdAt));
}

/**
 * The conversation `POST /api/chats/:id/messages` actually landed the message
 * in, when that is not the one it was addressed to.
 *
 * A worker loss reprovisions the assistant workspace behind the call and
 * answers on a *different* chat; the route reports it as `chatId`. Any
 * other answer — a bare `{ ok: true }` when nothing moved, or a non-assistant
 * chat — simply means "nothing moved".
 */
export function landedChatId(response: unknown, requestedChatId: string): string | undefined {
  if (typeof response !== "object" || response === null) return undefined;
  const landed = (response as { chatId?: unknown }).chatId;
  if (typeof landed !== "string" || landed.length === 0 || landed === requestedChatId) return undefined;
  return landed;
}

/** Falls back to the list when the open conversation is gone (archived, or a
 * reset retired the workspace and its threads with it). */
export function reconcileAssistantView(
  view: AssistantView,
  conversations: readonly AssistantConversation[],
): AssistantView {
  if (view.kind === "list") return view;
  return conversations.some((conversation) => conversation.chatId === view.chatId) ? view : { kind: "list" };
}

const SYSTEM_NOTICE_RE = /<rivets_system>([\s\S]*?)<\/rivets_system>/;

/**
 * `<rivets_system>` is how the server frames an automated notice (a watched
 * task finished, the workspace was replaced) inside an ordinary user message —
 * see `assistantWakeNotice` / `ASSISTANT_WORKSPACE_LOST_NOTICE`. It is Rivets
 * speaking, so it renders as a quiet system line, never as a user bubble. A
 * worker-loss notice is *prepended* to the user's own text, so both parts can
 * be present in one message.
 */
export function parseSystemNotice(text: string): { notice?: string; body: string } {
  const match = SYSTEM_NOTICE_RE.exec(text);
  if (!match) return { body: text };
  const notice = (match[1] ?? "").trim();
  const body = (text.slice(0, match.index) + text.slice(match.index + match[0].length)).trim();
  return { notice: notice.length > 0 ? notice : undefined, body };
}

/**
 * Whether the drawer offers to rewrite a message the assistant has not taken.
 *
 * Three things have to hold, and the third is the one that is easy to miss: a
 * `<rivets_system>` notice is the *server* telling the model why it is being
 * written to, wrapped around the author's words. An editor that let someone
 * replace the whole message would delete that framing without ever showing
 * them they had, so a message carrying one is read-only.
 */
export function canEditQueuedAssistantMessage(
  message: { text: string; userId: string; queuedBehindRun: boolean },
  currentUserId: string | undefined,
): boolean {
  if (!message.queuedBehindRun) return false;
  if (!currentUserId || message.userId !== currentUserId) return false;
  return parseSystemNotice(message.text).notice === undefined;
}

const TASK_ID_RE = /\bwt_[A-Za-z0-9][A-Za-z0-9_-]{1,40}\b/g;
/** Fenced code, inline code, and anything already inside a Markdown link. */
const PROTECTED_RE = /(```[\s\S]*?```|`[^`\n]*`|\[[^\]\n]*\]\([^)\n]*\))/g;

/**
 * Turns bare `wt_…` ids the assistant mentions into Markdown links onto the
 * ordinary worktree route. Ids inside code spans, fenced blocks or an existing
 * link are left alone, as is an id that is already part of a URL path.
 */
export function linkTaskReferences(text: string, orgId: string): string {
  if (!orgId) return text;
  return text
    .split(PROTECTED_RE)
    .map((segment, index) => {
      if (index % 2 === 1) return segment; // a captured protected span
      return segment.replace(TASK_ID_RE, (id, offset: number) => {
        const before = segment.slice(0, offset);
        return /[/=]$/.test(before) ? id : `[${id}](/o/${orgId}/wt/${id})`;
      });
    })
    .join("");
}

/**
 * The in-app path for a link the transcript rendered, or undefined when the
 * link points somewhere else. Used to keep a task reference a router
 * navigation instead of a full page load in a new tab.
 */
export function internalTaskPath(href: string, origin: string): string | undefined {
  let url: URL;
  try {
    url = new URL(href, origin);
  } catch {
    return undefined;
  }
  if (url.origin !== origin) return undefined;
  return /^\/o\/[^/]+\/wt\/[^/]+\/?$/.test(url.pathname) ? url.pathname + url.search : undefined;
}

/**
 * Whether anything worth a dot arrived past `lastSeenSeq`. The user's own
 * messages are excluded — sending is not a notification.
 */
export function hasUnreadReply(events: readonly ChatEvent[], lastSeenSeq: number): boolean {
  return events.some((event) => event.seq > lastSeenSeq && event.payload.kind !== "user.message");
}

/** Same compact scale the task list uses: "now", "4m", "3h", "2d", then a date. */
export function relativeAssistantTime(timestamp: number, now = Date.now()): string {
  const seconds = Math.max(0, (now - timestamp) / 1000);
  if (seconds < 60) return "now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(timestamp).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
